import { CommunityEvents, Event } from "@dreambigger/shared/src/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import useSWRInfinite from "swr/infinite";
import { organizationPaths } from "@dreambigger/shared/src/api/organizations";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";
import { Spin, Typography } from "@dreambigger/design-system";
import { EventCard, FundraiserCard } from "..";
import Masonry from "react-masonry-css";
import styles from "@dreambigger/shared/src/components/board/styles/react-masonry.module.scss";

import InfiniteScroll from "react-infinite-scroll-component";
import { BoardProps } from "./board-wrapper";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import {
  api,
  tokenHelper,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import {
  useMixpanel,
  useGA,
  useAppcues,
  useSegment,
} from "@dreambigger/shared/src/hooks";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import {
  boardFetcherFn,
  FilterDefaults,
  MASONRY_DEFAULTS_COLS,
} from "@dreambigger/shared/src/types/board";

const { Title } = Typography;

interface EventsBoardProps extends BoardProps {
  filterDefaults: FilterDefaults;
  initialData?: CommunityEvents[];
}

const EventsBoard: React.FC<EventsBoardProps> = ({
  community,
  initialData: initialEvents,
  filterDefaults,
}) => {
  const router = useRouter();

  // Load analytics hooks
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  // Load appcues hook
  const appcues = useAppcues();

  const [today] = useState(new Date().toISOString());

  const paginationLength =
    community?.meta?.feeds?.paginationLength || filterDefaults.pagination;

  const { data, setSize: setPageIndex } = useSWRInfinite<CommunityEvents>(
    (pageIndex) =>
      organizationPaths.getEvents(
        community?.organization.id,
        generateWeserveQueryParams({
          limit: String(paginationLength),
          sort: filterDefaults.sort,
          direction: filterDefaults.direction,
          offset: String(paginationLength * pageIndex),
          endAtGte: today,
        })
      ),
    boardFetcherFn,
    {
      revalidateFirstPage: false,
      fallbackData: initialEvents,
    }
  );

  const feedData = data || initialEvents;
  const [userLoading, { data: me }] = useUser(api, tokenHelper);

  const feed = useMemo(() => {
    if (!feedData) {
      return {
        records: [],
        totalRecords: 0,
      };
    }

    // useSWRInfinite returns data in an array of record arrays, i.e. =>
    // [{records: [{id: 1, ...}, {id: 2, ...}], totalRecords: 18}, {records: [{id: 2, ...}, {id: 3, ...}], totalRecords: 18}]
    // We need to flatten these records to create 1 array of records.
    const totalRecords: number = feedData?.[0]?.totalRecords || 0;

    const records = feedData.reduce(
      (previousValue, currentData) => [
        ...previousValue,
        ...(currentData?.records || []),
      ],
      [] as Event[]
    );

    return {
      records,
      totalRecords,
    };
  }, [feedData]);

  const cards = useMemo(
    () =>
      feed.records.map((event) =>
        event.isFundraiser ? (
          <FundraiserCard
            key={event.id}
            feedItem={event}
            community={community}
            currentUser={me}
          />
        ) : (
          <EventCard key={event.id} feedItem={event} currentUser={me} />
        )
      ),
    [feed]
  );

  const loadNextPage = useCallback(() => {
    // Load additional content and record a pagination change.
    setPageIndex((val) => {
      googleAnalytics.event({
        category: `Feed Pagination`,
        action: `Loaded page ${val + 1}`,
        label: "Infinite Scroll",
      });
      return val + 1;
    });
  }, [setPageIndex]);

  // Track the page view for analytics and appcues
  useEffect(() => {
    // Give GoogleAnalytics time to initialize
    setTimeout(() => {
      // Wait for a response on whether a user is logged in.
      if (userLoading) {
        return;
      }
      googleAnalytics.setDefaultDimensions(community.slug, me);
      googleAnalytics.pageview(window.location.pathname);
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.PAGE_VIEW,
        properties: {
          Page: window.location.pathname,
          "Page Type": InspireAnalyticsHelper.getPageType(router.route),
          "Host Name": window.location.host,
        },
        mixpanel,
        segment,
      });
      // If there is a user, track user page visit on appcues
      if (me?.id) {
        appcues.identify(me.id.toString());
      }
    }, 500);
  }, [userLoading]);

  const isEmpty = feed.records.length === 0;

  const hasMoreRecords =
    !isEmpty && feed.records && feed.totalRecords > feed.records.length;

  //  If the community has a keywordUpper value, use the keywordUpper value with an "s" appended at the end
  //  otherwise default to using EVENTS as the word to display in the header text.
  //  For example, Thrivent will display "PROJECTS" while a community with no keywordUpper value will default to "EVENTS"
  const keywordUpper = community?.meta?.marketing?.keywordUpper?.toUpperCase();
  const eventsLabel = keywordUpper ? `${keywordUpper}S` : "EVENTS";
  const title = `${community?.organization?.name.toUpperCase()} ${eventsLabel.toUpperCase()}`;

  return (
    <>
      <Title>{title}</Title>
      <div className="pt-5">
        <InfiniteScroll
          dataLength={cards.length}
          next={loadNextPage}
          hasMore={hasMoreRecords}
          loader={
            <Spin
              size="large"
              className="w-100 mv-9 primary"
              tip="Loading More..."
            />
          }
          className="overflow-visible"
        >
          <Masonry
            breakpointCols={MASONRY_DEFAULTS_COLS}
            className={styles.grid}
            columnClassName={styles.column}
          >
            {cards}
          </Masonry>
        </InfiniteScroll>
      </div>
    </>
  );
};

export default EventsBoard;
